// Sandbox-status pill. Phase 6.
//
// Renders the richer `SandboxPhase` the orchestrator emits (idle →
// triggering → waiting-for-runner → booting → ready → degraded /
// suspended / terminated). The `ready` pill auto-hides after a short
// delay so the chat header is uncluttered during normal use; non-ready
// phases stay pinned.
//
// Clicking the pill opens a dropdown with a `restart sandbox` action;
// degraded / terminated states additionally surface `retry`.

import { useEffect, useState } from "react";

import type { SandboxStatusSnapshot } from "../state/sandbox-status-store";
import { BADGE_TONE, MENU_ITEM, MENU_PANEL, type BadgeTone } from "../ui/classes";

const READY_FADE_MS = 3_000;

interface PillVisual {
  readonly label: string;
  readonly tone: BadgeTone;
  readonly pinned: boolean;
}

const visualForPhase = (
  snapshot: SandboxStatusSnapshot,
): PillVisual | null => {
  const label = (
    fallback: string,
  ): string => snapshot.message ?? fallback;
  switch (snapshot.phase) {
    case "idle":
      return null;
    case "triggering":
      return {
        label: label("Starting…"),
        tone: "neutral",
        pinned: true,
      };
    case "waiting-for-runner":
      return {
        label: label("Getting things ready…"),
        tone: "neutral",
        pinned: true,
      };
    case "booting":
      return {
        label: label("loading tools…"),
        tone: "confirm",
        pinned: true,
      };
    case "ready":
      return {
        label: "Ready",
        tone: "success",
        pinned: false,
      };
    case "degraded":
      return {
        label: label("reconnecting…"),
        tone: "caution",
        pinned: true,
      };
    case "suspended":
      return {
        label: label("Paused"),
        tone: "neutral",
        pinned: true,
      };
    case "terminated":
      return {
        label: label("Stopped"),
        tone: "danger",
        pinned: true,
      };
  }
};

export interface SandboxStatusProps {
  readonly sandbox: SandboxStatusSnapshot;
  /** Called when the user opens the menu and picks `restart`. */
  readonly onRestart?: () => void;
  /** Called for the `retry` action surfaced from degraded / terminated. */
  readonly onRetry?: () => void;
  /** Pending state on the retry / restart action — disables the buttons. */
  readonly pending?: boolean;
}

export function SandboxStatus({
  sandbox,
  onRestart,
  onRetry,
  pending = false,
}: SandboxStatusProps) {
  const visual = visualForPhase(sandbox);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setHidden(false);
    if (sandbox.phase !== "ready") return;
    const handle = window.setTimeout(() => setHidden(true), READY_FADE_MS);
    return () => window.clearTimeout(handle);
  }, [sandbox.phase, sandbox.message]);

  if (visual === null) return null;
  if (hidden && !visual.pinned) return null;

  const showRetry =
    sandbox.phase === "degraded" || sandbox.phase === "terminated";

  return (
    <div className="relative inline-flex" data-testid="sandbox-status">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${BADGE_TONE[visual.tone]}`}
        data-phase={sandbox.phase}
      >
        <span aria-hidden="true" className="block h-1.5 w-1.5 rounded-full bg-current opacity-70" />
        <span>{visual.label}</span>
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Session actions"
          className={`absolute left-0 top-full min-w-[10rem] text-sm ${MENU_PANEL}`}
        >
          {showRetry && onRetry !== undefined && (
            <button
              type="button"
              role="menuitem"
              disabled={pending}
              onClick={() => {
                setOpen(false);
                onRetry();
              }}
              className={`${MENU_ITEM} disabled:opacity-50`}
            >
              Retry
            </button>
          )}
          {onRestart !== undefined && (
            <button
              type="button"
              role="menuitem"
              disabled={pending}
              onClick={() => {
                setOpen(false);
                onRestart();
              }}
              className={`${MENU_ITEM} disabled:opacity-50`}
            >
              Restart
            </button>
          )}
        </div>
      )}
    </div>
  );
}
